import React from 'react';
import PropTypes from 'prop-types';
import { ScrollRestoration } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap5';

import formatCurrency from 'components/utils/formatCurrency';
import billingTypes from 'data/billing_types';
import Description from './Description';
import PolicyIcon from './PolicyIcon';
import SummaryDownloadQuote from './SummaryDownloadQuote';
import SummaryDownload from './SummaryDownload';

const pricyByCurrency = (abbr) => {
  if (abbr === 'GBP' || abbr === 'CHF') {
    return 100;
  } if (abbr === 'USD') {
    return 100;
  }
  return 135;
};

export default function Summary({ plan }) {
  const annualDiscountPresent = plan.attributes.payment_mode === 'annual' && plan.attributes.annual_discount !== '';
  const discountsPresent = plan.attributes.monthly_discount_enable || annualDiscountPresent;

  const abbr = plan.attributes.currency.title;
  const { mark } = plan.attributes.currency;

  const isFree = plan?.attributes?.is_free || false;

  return (
    <>
      {plan.attributes.status === 'draft' && <SummaryDownloadQuote plan={plan} />}
      {plan.attributes.status !== 'draft' && <SummaryDownload plan={plan} />}

      <section>
        <div className="d-flex align-items-center mb-3">
          <h2 className="mb-0 me-2">{plan.attributes.policy_template_name}</h2>
          <PolicyIcon plan={plan} />
        </div>

        <Row>
          <Col md={6}>
            <Description groups={plan.attributes.description_left_column} />
          </Col>
          <Col md={6}>
            <Description groups={plan.attributes.description_right_column} />
          </Col>
        </Row>
      </section>

      <section>
        <Row>
          <Col md="10" lg="8">
            <div className="fs-4">{plan.attributes.name}</div>

            <div className="mt-3">
              Coaching Sessions Per Employee:

              <div className="fs-5">
                {plan.attributes.sessions === 0 ? 'Assessment Only' : plan.attributes.sessions}
              </div>
            </div>

            {!isFree && plan.attributes.payment_information_id !== null && (
            <div className="mt-3">
              Payment Method:

              <div className="fs-5">
                {plan.attributes.payment_information_label}
              </div>
            </div>
            )}

            {!isFree && (
            <div className="mt-3">
              Billing type:

              <div className="fs-5">
                {billingTypes.find((item) => item.value === plan.attributes.payment_mode).label}
              </div>
            </div>
            )}

            <div className="mt-3">
              Effective date:

              <div className="fs-5">
                {plan.attributes.effective_date_form}
              </div>
            </div>

            <div className="mt-3">
              Renewal date:

              <div className="fs-5">
                {plan.attributes.renewal_date_form}
              </div>
            </div>

            <div className="mt-3">
              Members:

              <div className="fs-5">
                {plan.attributes.employees_covered}
              </div>
            </div>

            {plan.attributes.monthly_custom_records.length > 0 && (
              <div className="mt-3">
                Additional Services:

                {plan.attributes.monthly_custom_records.map((item) => (
                  <div key={item.id} className="d-flex justify-content-between align-items-center mt-1">
                    <small>{item.name}</small>

                    <small>
                      <span className="me-1 fs-5">
                        {mark}
                        {item.price}
                      </span>

                      <span className="fw-light">{abbr}</span>
                    </small>
                  </div>
                ))}
              </div>
            )}

            <hr />

            {discountsPresent && (
              <div className="mt-3">
                Discounts:

                {plan.attributes.monthly_discount_enable && (
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small>{plan.attributes.monthly_discount_name}</small>

                    <small className="ms-1">
                      -
                      {' '}
                      {plan.attributes.monthly_discount}
                      %
                    </small>
                  </div>
                )}

                {annualDiscountPresent && (
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small>Annual Discount</small>

                    <small className="ms-1">
                      -
                      {' '}
                      {plan.attributes.annual_discount}
                      %
                    </small>
                  </div>
                )}
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>PEPM:</div>

              <div className="ms-1">
                {isFree ? 'Free' : formatCurrency(plan.attributes.price_employee_month, abbr)}
              </div>
            </div>

            {!isFree && (
              <small key="pepm-notice">
                Minimum
                  {' '}
                  {mark}
                  {pricyByCurrency(plan.attributes.currency.title)}
                /month
              </small>
            )}

            {plan.attributes.is_pay_per_used && (
              <>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>PPS:</div>

                  <div className="ms-1">
                    {formatCurrency(plan.attributes.price_session, abbr)}
                  </div>
                </div>
                <div className="mt-3">
                  PAY AS YOU GO
                </div>
              </>
            )}

            <div className="d-flex justify-content-between align-items-center mt-3 fs-4">
              <div>PREMIUM:</div>

              <div className="ms-1">
                {isFree ? 'Free' : formatCurrency(plan.attributes.monthly_total, abbr)}
              </div>
            </div>

            {plan.attributes.onetime_custom_records.length > 0 && (
              <div className="mt-3">
                One-Time Premium

                {plan && plan.attributes.onetime_custom_records.map((item) => (
                  <div key={item.id} className="d-flex justify-content-between align-items-center mt-1">
                    <small>{item.name}</small>

                    <small>
                      <span className="me-1 fs-5">
                        {mark}
                        {formatCurrency(parseFloat(item.price) * (
                          item.is_activation_fee && plan.attributes.activation_fee_type === 'member'
                            ? parseFloat(plan.attributes.employees_covered)
                            : 1
                        ), '')}
                      </span>

                      <span className="fw-light">{abbr}</span>
                    </small>
                  </div>
                ))}

                {plan.attributes.onetime_discount !== '' && (
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small>{plan.attributes.onetime_discount_name}</small>

                    <small className="ms-1">
                      {'- '}
                      {plan.attributes.onetime_discount}
                      %
                    </small>
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>
      </section>

      <ScrollRestoration />
    </>
  );
}

Summary.propTypes = {
  plan: PropTypes.object.isRequired,
};
